// ─── DATE/TIME HELPERS ────────────────────────────────────────────────────────
// Pure date and formatting helpers shared across modules. Weeks are Mon-based.

export function toLocalDateStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function startOfWeek(d) {
  const s = new Date(d);
  s.setDate(s.getDate() - ((s.getDay() + 6) % 7)); // Mon-based
  s.setHours(0, 0, 0, 0);
  return s;
}

export function endOfWeek(d) {
  const e = startOfWeek(d);
  e.setDate(e.getDate() + 6);
  e.setHours(23, 59, 59, 999);
  return e;
}

export function nextStartOfWeek(d) {
  const s = startOfWeek(d);
  s.setDate(s.getDate() + 7);
  return s;
}

export function fmtWeekRange(start) {
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return fmtDateRange(start, end);
}

export function fmtDateRange(start, end) {
  const fmt = d => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${fmt(start)} – ${fmt(end)}`;
}

export function fmtTime(dateTimeStr) {
  if (!dateTimeStr) return '';
  const d = new Date(dateTimeStr);
  if (isNaN(d)) return '';
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

// Compact display time — "8:30a" / "10a" (no leading zero, minutes dropped
// when exactly on the hour, single-letter meridiem). A display format only —
// the underlying event time never changes — used by default on Home's event
// rows so titles get more of the row's width.
export function fmtTimeCompact(dateTimeStr) {
  if (!dateTimeStr) return '';
  const d = new Date(dateTimeStr);
  if (isNaN(d)) return '';
  const h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? 'p' : 'a';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h12}${ampm}` : `${h12}:${String(m).padStart(2, '0')}${ampm}`;
}

// "Mon 7/6" — from a yyyy-mm-dd string
export function fmtDayDate(isoStr) {
  const d = new Date(isoStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' });
}

// yyyy-mm-dd of a GCal event's start (all-day or timed)
export function eventDate(e) {
  return (e.start?.dateTime || e.start?.date || '').split('T')[0];
}

// yyyy-mm-dd of the last calendar day a GCal event covers. All-day events use
// Google's exclusive `end.date` convention (a Mon-Wed all-day event has
// end.date = Thursday), so this steps back one day; a timed event's end is a
// precise instant, so its date part is already inclusive. Falls back to the
// start date when there's no end (e.g. synthetic single-day rows).
export function eventEndDate(e) {
  if (e.end?.date) {
    const d = new Date(e.end.date + 'T12:00:00');
    d.setDate(d.getDate() - 1);
    return toLocalDateStr(d);
  }
  return (e.end?.dateTime || '').split('T')[0] || eventDate(e);
}

// Every yyyy-mm-dd a GCal event spans, start through end inclusive — a
// single-day event returns one date, a multi-day (usually all-day) event
// returns each day it covers.
export function eventDates(e) {
  const start = eventDate(e);
  if (!start) return [];
  const end = eventEndDate(e) || start;
  if (end <= start) return [start];
  const dates = [];
  const cursor = new Date(start + 'T12:00:00');
  const last = new Date(end + 'T12:00:00');
  while (cursor <= last) {
    dates.push(toLocalDateStr(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
}

// Whether a GCal event covers the given yyyy-mm-dd (start..end inclusive) —
// use this instead of `eventDate(e) === isoDate` wherever a multi-day event
// needs to keep showing up on the days after it started.
export function eventOverlapsDate(e, isoDate) {
  if (!isoDate) return false;
  const start = eventDate(e);
  if (!start) return false;
  const end = eventEndDate(e) || start;
  return isoDate >= start && isoDate <= end;
}

// "45 min" / "1 hr 20 min" between now and a future Date, clamped to "now"
// once targetDate has already passed.
export function fmtDurationUntil(targetDate, now = new Date()) {
  const ms = targetDate - now;
  if (isNaN(ms) || ms <= 0) return 'now';
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
}

// Human "time until" an event for coverage texts: "in 90 min", "in 1 hr 20 min",
// "in 2 days", "tomorrow", "today", or "now" once it's started. GCal-style
// `start` — { dateTime } for timed events, { date } for all-day.
export function fmtTimeToEvent(start, now = new Date()) {
  if (!start) return '';
  const allDay = !start.dateTime;
  const target = new Date(allDay ? `${start.date}T00:00:00` : start.dateTime);
  if (isNaN(target)) return '';

  // Calendar-day difference at local midnight — so "tomorrow" reads right near midnight.
  const midnight = d => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
  const dayDiff = Math.round((midnight(target) - midnight(now)) / 86400000);

  if (allDay) {
    if (dayDiff <= 0) return 'today';
    if (dayDiff === 1) return 'tomorrow';
    return `in ${dayDiff} days`;
  }
  if (target <= now) return 'now';
  if (dayDiff >= 2) return `in ${dayDiff} days`;
  if (dayDiff === 1) return 'tomorrow';
  return `in ${fmtDurationUntil(target, now)}`;
}

// Display-ready time range for a GCal event — "All day", "9:00 AM", or
// "9:00–10:30 AM". Pure function of the raw event, shared by Review & Assign's
// day cards and the standalone schedule-snapshot builder (api/scheduleSnapshot.js).
export function formatEventTime(evt) {
  if (evt.start?.date) return 'All day';
  if (!evt.start?.dateTime) return '';
  const d = new Date(evt.start.dateTime);
  const end = evt.end?.dateTime ? new Date(evt.end.dateTime) : null;
  const fmt = (dt) => dt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return end ? `${fmt(d)}–${fmt(end)}` : fmt(d);
}

// "16:00" (24h) -> "4:00 PM" — the display-side counterpart to
// parseClockTime below. Plain clock-time formatting, not tied to a date, so
// it works for values that never had a real Date (session startTime/endTime
// fields, form input while typing, etc.).
export function fmtClockTime(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return '';
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

// "3:15 PM" / "15:15" → { h, m } in 24h, or null when unparseable
export function parseClockTime(str) {
  if (!str) return null;
  const m = String(str).trim().match(/^(\d{1,2}):(\d{2})\s*(am|pm)?$/i);
  if (!m) return null;
  let h = Number(m[1]);
  const min = Number(m[2]);
  if (isNaN(h) || isNaN(min) || h > 23 || min > 59) return null;
  const ampm = m[3]?.toLowerCase();
  if (ampm === 'pm' && h < 12) h += 12;
  if (ampm === 'am' && h === 12) h = 0;
  return { h, m: min };
}
